import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { listDocs } from "@/integrations/firebase/data";
import {
  saveDealership, listWebhookTokens, rotateWebhookToken, addDid as addDidOp, removeDid as removeDidOp,
  createClientUser, setUserClient, setUserRole, backfillIvrFields,
} from "@/integrations/firebase/admin-ops";
import { useAuth } from "@/hooks/use-auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Copy, RefreshCw, Trash2, UserPlus } from "lucide-react";


export const Route = createFileRoute("/_app/admin")({
  head: () => ({ meta: [{ title: "Admin & users — EESWEB" }] }),
  component: AdminPage,
});

type ClientRow = { id: string; name: string; slug: string };
type ProfileRow = { id: string; full_name: string | null; email?: string | null; role: "admin" | "client"; clientId: string | null };
type DidRow = { id: string; clientId: string; did_number: string; label: string | null };

const errMsg = (e: unknown, fallback = "Failed") => (e instanceof Error ? e.message : fallback);

function AdminPage() {
  const { role, loading, user } = useAuth();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [didInputs, setDidInputs] = useState<Record<string, { did: string; label: string }>>({});
  const [newUser, setNewUser] = useState({ email: "", password: "", fullName: "", clientId: "" });
  const [creatingUser, setCreatingUser] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState<Awaited<ReturnType<typeof backfillIvrFields>> | null>(null);

  const isAdmin = role === "admin";
  const { data: clients } = useQuery({
    queryKey: ["admin-clients"],
    enabled: isAdmin,
    queryFn: async () => (await listDocs<ClientRow>("clients")).sort((a, b) => String(a.name).localeCompare(String(b.name))),
  });
  const { data: webhookTokens } = useQuery({
    queryKey: ["admin-webhook-tokens"],
    enabled: isAdmin,
    queryFn: listWebhookTokens,
  });
  const { data: users } = useQuery({
    queryKey: ["admin-users"],
    enabled: isAdmin,
    queryFn: async () => (await listDocs<ProfileRow>("profiles")).sort((a, b) => String(a.full_name ?? a.email ?? "").localeCompare(String(b.full_name ?? b.email ?? ""))),
  });
  const { data: dids } = useQuery({
    queryKey: ["admin-dids"],
    enabled: isAdmin,
    queryFn: async () => (await listDocs<DidRow>("client_dids")).sort((a, b) => a.did_number.localeCompare(b.did_number)),
  });
  const tokenById = new Map((webhookTokens ?? []).map((t) => [t.id, t.webhook_token]));

  if (loading) return null;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const globalUrl = `${origin}/api/public/smartflo`;

  const runBackfill = async () => {
    setBackfilling(true);
    setBackfillResult(null);
    try {
      const res = await backfillIvrFields();
      setBackfillResult(res);
      toast.success(`Backfill complete: ${res.updated} call(s) updated.`);
      for (const k of ["ivr-calls", "calls-list", "kpis", "charts"]) qc.invalidateQueries({ queryKey: [k] });
    } catch (e) {
      toast.error(errMsg(e, "Backfill failed"));
    } finally {
      setBackfilling(false);
    }
  };

  const addClient = async () => {
    if (!name || !slug) return toast.error("Name and slug required");
    if (clients?.some((c) => c.slug === slug)) return toast.error(`Slug "${slug}" is already used`);
    try {
      await saveDealership({ name, slug });
      setName(""); setSlug("");
      qc.invalidateQueries({ queryKey: ["admin-clients"] });
      qc.invalidateQueries({ queryKey: ["admin-webhook-tokens"] });
      toast.success("Client added");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const rotateToken = async (id: string) => {
    try {
      await rotateWebhookToken(id);
      qc.invalidateQueries({ queryKey: ["admin-webhook-tokens"] });
      toast.success("Token rotated");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const copy = (t: string) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  const assignClient = async (userId: string, clientId: string) => {
    try {
      await setUserClient(userId, clientId === "none" ? null : clientId);
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      toast.success("Updated");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const toggleAdmin = async (userId: string, currentlyAdmin: boolean) => {
    if (currentlyAdmin && userId === user?.uid) return toast.error("You can't remove your own admin access");
    try {
      await setUserRole(userId, currentlyAdmin ? "client" : "admin");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      toast.success("Role updated");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const addDid = async (clientId: string) => {
    const v = didInputs[clientId];
    try {
      await addDidOp({ clientId, didNumber: v?.did ?? "", label: v?.label });
      setDidInputs((s) => ({ ...s, [clientId]: { did: "", label: "" } }));
      qc.invalidateQueries({ queryKey: ["admin-dids"] });
      toast.success("DID added");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const removeDid = async (id: string) => {
    try {
      await removeDidOp(id);
      qc.invalidateQueries({ queryKey: ["admin-dids"] });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const createUser = async () => {
    if (!newUser.email || !newUser.password || !newUser.fullName || !newUser.clientId) {
      return toast.error("Fill in all fields");
    }
    setCreatingUser(true);
    try {
      await createClientUser(newUser);
      toast.success(`Client user ${newUser.email} created. They can sign in now.`);
      setNewUser({ email: "", password: "", fullName: "", clientId: "" });
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    } catch (e) {
      toast.error(errMsg(e, "Failed to create user"));
    } finally {
      setCreatingUser(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">Admin &amp; users</h1>
        <p className="text-sm text-muted-foreground">Connect Smartflo, manage dealerships and their phone numbers, and give people access.</p>
      </div>

      <div className="pt-2">
        <h2 className="font-display text-lg font-semibold tracking-tight">Dealerships</h2>
        <p className="text-sm text-muted-foreground">Each dealership's calls are matched by the DID (Smartflo number) they were dialled on.</p>
      </div>

      <Card className="shadow-soft">
        <CardHeader>
          <CardTitle className="text-base">Smartflo webhook (one-time setup)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Paste this single URL into Smartflo. Calls are routed to the correct client automatically
            by matching the incoming <span className="mono">$did_number</span> to a DID below.
          </p>
          <div className="flex gap-2 items-center">
            <code className="flex-1 text-xs mono bg-muted/40 p-2 rounded truncate">{globalUrl}</code>
            <Button size="icon" variant="ghost" onClick={() => copy(globalUrl)}><Copy className="size-3.5" /></Button>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-soft">
        <CardHeader><CardTitle className="text-base">Add a dealership</CardTitle></CardHeader>
        <CardContent className="flex flex-col sm:flex-row gap-2 sm:items-end">
          <div className="flex-1"><Label htmlFor="new-client-name">Name</Label><Input id="new-client-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Corp" /></div>
          <div className="flex-1"><Label htmlFor="new-client-slug">Slug</Label><Input id="new-client-slug" value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))} placeholder="acme" /></div>
          <Button onClick={addClient}>Add</Button>
        </CardContent>
      </Card>

      <Card className="shadow-soft">
        <CardHeader><CardTitle className="text-base">Dealerships and their DIDs</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {clients?.map((c) => {
            const url = `${origin}/api/public/smartflo/${tokenById.get(c.id) ?? "…"}`;
            const clientDids = dids?.filter((d) => d.clientId === c.id) ?? [];
            const inp = didInputs[c.id] ?? { did: "", label: "" };
            return (
              <div key={c.id} data-testid={`client-card-${c.slug}`} className="space-y-3 rounded-xl border bg-muted/20 p-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{c.name}</div>
                    <div className="text-xs text-muted-foreground mono">{c.slug}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => rotateToken(c.id)}>
                    <RefreshCw className="size-3 mr-1" /> Rotate token
                  </Button>
                </div>

                <div>
                  <div className="mb-1.5 text-xs font-medium text-muted-foreground">Smartflo numbers (DIDs)</div>
                  <div className="space-y-1">
                    {clientDids.map((d) => (
                      <div key={d.id} className="flex items-center gap-2 text-sm">
                        <code className="mono bg-muted/40 px-2 py-1 rounded">{d.did_number}</code>
                        {d.label && <span className="text-muted-foreground">{d.label}</span>}
                        <Button size="icon" variant="ghost" className="ml-auto h-7 w-7" aria-label={`Remove DID ${d.did_number}`} onClick={() => removeDid(d.id)}>
                          <Trash2 className="size-3.5" />
                        </Button>
                      </div>
                    ))}
                    {clientDids.length === 0 && (
                      <div className="text-xs text-muted-foreground">No DIDs yet — calls to this client won't route until you add one.</div>
                    )}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 mt-2">
                    <Input
                      placeholder="DID number (e.g. 918045678900)"
                      className="flex-1"
                      value={inp.did}
                      onChange={(e) => setDidInputs((s) => ({ ...s, [c.id]: { ...inp, did: e.target.value } }))}
                    />
                    <Input
                      placeholder="Label (optional)"
                      className="flex-1"
                      value={inp.label}
                      onChange={(e) => setDidInputs((s) => ({ ...s, [c.id]: { ...inp, label: e.target.value } }))}
                    />
                    <Button onClick={() => addDid(c.id)}>Add DID</Button>
                  </div>
                </div>

                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground">Per-client URL (fallback)</summary>
                  <div className="flex gap-2 items-center mt-2">
                    <code className="flex-1 mono bg-muted/40 p-2 rounded truncate">{url}</code>
                    <Button size="icon" variant="ghost" onClick={() => copy(url)}><Copy className="size-3.5" /></Button>
                  </div>
                </details>
              </div>
            );
          })}
          {(!clients || clients.length === 0) && <div className="text-sm text-muted-foreground">No clients yet.</div>}
        </CardContent>
      </Card>

      <div className="pt-2">
        <h2 className="font-display text-lg font-semibold tracking-tight">People</h2>
        <p className="text-sm text-muted-foreground">Dealership users only ever see their own dealership's calls.</p>
      </div>

      <Card className="shadow-soft">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <UserPlus className="size-4" /> Create a dealership login
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Creates a login that sees only the chosen dealership. Share the email and password with them directly.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label htmlFor="nu-name">Full name</Label><Input id="nu-name" value={newUser.fullName} onChange={(e) => setNewUser({ ...newUser, fullName: e.target.value })} placeholder="Jane Doe" /></div>
            <div>
              <Label>Client</Label>
              <Select value={newUser.clientId} onValueChange={(v) => setNewUser({ ...newUser, clientId: v })}>
                <SelectTrigger aria-label="New user client"><SelectValue placeholder="Choose a client" /></SelectTrigger>
                <SelectContent>
                  {clients?.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label htmlFor="nu-email">Email</Label><Input id="nu-email" type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} placeholder="jane@acme.com" /></div>
            <div><Label htmlFor="nu-password">Password</Label><Input id="nu-password" type="text" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} placeholder="At least 8 characters" /></div>
          </div>
          <Button onClick={createUser} disabled={creatingUser}>
            {creatingUser ? "Creating…" : "Create user"}
          </Button>
        </CardContent>
      </Card>

            <Card className="shadow-soft">
        <CardHeader><CardTitle className="text-base">Everyone with access</CardTitle></CardHeader>

        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b text-left text-xs font-medium text-muted-foreground">
              <tr><th className="p-2 font-medium">Person</th><th className="p-2 font-medium">Dealership</th><th className="p-2 font-medium">Access</th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {users?.map((u) => {
                const uIsAdmin = u.role === "admin";
                return (
                  <tr key={u.id} data-testid={`user-row-${u.email ?? u.id}`}>
                    <td className="p-2">
                      <div>{u.full_name ?? <span className="mono text-xs">{u.id.slice(0, 8)}</span>}</div>
                      {u.email && <div className="text-xs text-muted-foreground">{u.email}</div>}
                    </td>
                    <td className="p-2">
                      <Select value={u.clientId ?? "none"} onValueChange={(v) => assignClient(u.id, v)}>
                        <SelectTrigger className="h-9 w-44 sm:w-56" aria-label={`Client for ${u.email ?? u.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Not assigned</SelectItem>
                          {clients?.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-2">
                      <Button size="sm" variant={uIsAdmin ? "default" : "outline"} onClick={() => toggleAdmin(u.id, uIsAdmin)}>
                        {uIsAdmin ? "Admin" : "Make admin"}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <div className="pt-2">
        <h2 className="font-display text-lg font-semibold tracking-tight">Maintenance</h2>
        <p className="text-sm text-muted-foreground">Safe to run any time.</p>
      </div>

<Card className="shadow-soft">
        <CardHeader>
          <CardTitle className="text-base">Data maintenance</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Backfill the IVR routing fields (branch, department, employee, workshop type, main
            menu, key path, routing result) on existing calls by re-reading the original Smartflo
            DTMF input from each call's raw payload. Only calls without a key path are touched,
            so this is safe to run repeatedly.
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={runBackfill} disabled={backfilling}>
              {backfilling ? "Backfilling…" : "Backfill IVR fields"}
            </Button>
            {backfillResult && (
              <div className="text-sm text-muted-foreground">
                Scanned <span className="font-medium text-foreground">{backfillResult.scanned}</span> call(s)
                across {backfillResult.clients} client(s); updated{" "}
                <span className="font-medium text-foreground">{backfillResult.updated}</span>.
              </div>
            )}
          </div>
          {backfillResult && backfillResult.perClient.length > 0 && (
            <ul className="text-xs text-muted-foreground space-y-1">
              {backfillResult.perClient
                .filter((p) => p.scanned > 0 || p.updated > 0)
                .map((p) => (
                  <li key={p.clientName} className="font-mono">
                    {p.clientName}: {p.updated}/{p.scanned} updated
                  </li>
                ))}
            </ul>
          )}
        </CardContent>
      </Card>

    </div>
  );
}
